import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../utils/apiClient';
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table';
import Swal from 'sweetalert2';
import { SERVER_BASE } from '../config/server';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faEdit, faTrash, faPlus, faPencil } from "@fortawesome/free-solid-svg-icons";
import moment from 'moment';
const LedgerGroupPage = ({ permissions }) => {
  const [ledgerGroups, setLedgerGroups] = useState([]);
  const [ledgerGroupName, setLedgerGroupName] = useState('');
  const [ledgerGroupCode, setLedgerGroupCode] = useState('');
  const [editIndex, setEditIndex] = useState(null);
  const code = localStorage.getItem('code');
  const role = localStorage.getItem('role')
  const token = localStorage.getItem('token'); // Retrieve the token from localStorage
  const email = localStorage.getItem('email');
  const [loadingLedgerGroups, setLoadingLedgerGroups] = useState(true);

  useEffect(() => {
    fetchLedgerGroups();
  }, []);

  const fetchLedgerGroups = async () => {
    try {
      setLoadingLedgerGroups(true); // ⏳ start loading
      const response = await apiClient.get(SERVER_BASE + '/ledger-groups', {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { code },
      });
      setLedgerGroups(response.data);
    } catch (error) {
      console.error('Error fetching ledger groups:', error);
    } finally {
      setLoadingLedgerGroups(false); // ✅ stop loading
    }
  };

  const handleLedgerGroupChange = (e) => {
    setLedgerGroupName(e.target.value);
  };
  const handleLedgerGroupCodeChange = (e) => {
    setLedgerGroupCode(e.target.value);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (ledgerGroupName.trim() === '') {
      Swal.fire('Please enter a ledger group name', '', 'warning');
      return;
    }
    if (ledgerGroupCode.trim() === '') {
      Swal.fire('Please enter a ledger group code', '', 'warning');
      return;
    }

    // Get token from localStorage
    const token = localStorage.getItem('token');

    if (!token) {
      Swal.fire('Unauthorized: No token found.', '', 'error');
      return;
    }

    Swal.fire({
      title: editIndex !== null ? 'Confirm Update' : 'Confirm Create',
      text: editIndex !== null
        ? 'Are you sure you want to update this ledger group?'
        : 'Are you sure you want to create this ledger group?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          let responseMessage = '';

          if (editIndex !== null) {
            // Update ledger group
            const response = await apiClient.put(
              `${SERVER_BASE}/updateLedgerGroup/${ledgerGroups[editIndex].id}`,
              {
                name: ledgerGroupName,
                groupCode: ledgerGroupCode,
                code,
                email
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`, // Include token in headers
                },
              }
            );
            responseMessage = response.data.message || 'Ledger Group updated successfully';
          } else {
            // Create ledger group
            const response = await apiClient.post(
              `${SERVER_BASE}/addLedgerGroup`,
              {
                name: ledgerGroupName,
                groupCode: ledgerGroupCode,
                code,
                email
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`, // Include token in headers
                },
              }
            );
            responseMessage = response.data.message || 'Ledger Group created successfully';
          }

          setLedgerGroupCode('');
          setLedgerGroupName('');
          setEditIndex(null);
          fetchLedgerGroups();

          Swal.fire({
            icon: 'success',
            title: editIndex !== null ? 'Updated!' : 'Created!',
            text: responseMessage,
            position: 'top-end',
            toast: true,
            timer: 2000,
            showConfirmButton: false,
          });
        } catch (error) {
          console.error('Error saving ledger group:', error);
          Swal.fire({
            icon: 'error',
            text: error.response?.data || 'There was an error processing your request.',
            position: 'top-end',
            toast: true,
            timer: 2000,
            showConfirmButton: false,
          });
        }
      }
    });
  };

  const handleEdit = (index) => {
    setLedgerGroupName(ledgerGroups[index].groupName);
    setLedgerGroupCode(ledgerGroups[index].groupCode);
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this ledger group!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await apiClient.delete(`${SERVER_BASE}/deleteLedgerGroup/${ledgerGroups[index].id}`, {
            data: { code },
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          fetchLedgerGroups();
          Swal.fire('Deleted!', 'Your ledger group has been deleted.', 'success');
        } catch (error) {
          console.error('Error deleting ledger group:', error);
        }
      }
    });
  };

  // Define columns for react-table
  const columns = useMemo(
    () => [
      {
        Header: 'S.No',
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: 'Ledger Group Name',
        accessor: 'groupName', // Access the 'name' field in the data
      },
      {
        Header: 'Ledger Group Code',
        accessor: 'groupCode', // Access the 'name' field in the data
      },
      {
        Header: "#",
        accessor: "Action",
        Cell: ({ row }) => {
          const {
            createdAt,
            createdByName,
            updatedAt,
            updatedByName,
          } = row.original;
          return (
            <div className="dropdown">
              <button
                className="btn btn-sm btn-light"
                type="button"
                id={`dropdownMenuButton-${row.index}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul
                className="dropdown-menu"
                aria-labelledby={`dropdownMenuButton-${row.index}`}
              >
                {(role === "itManager" || permissions?.edit) && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleEdit(row.index)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-2" />
                      Edit
                    </button>
                  </li>
                )}

                {(role === "itManager" || permissions?.delete) && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleDelete(row.index)}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Delete
                    </button>
                  </li>
                )}
                {(createdByName || updatedByName) && (
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                )}
                {createdByName && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPlus} /> {createdByName}
                    {createdAt &&
                      ` (${moment(createdAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}

                {/* Updated Info */}
                {updatedByName && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPencil} /> {updatedByName}
                    {updatedAt &&
                      ` (${moment(updatedAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}
              </ul>
            </div>
          );
        },
      }
    ],
    [ledgerGroups]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data: ledgerGroups,
      initialState: { pageIndex: 0, pageSize: 20 }, // min-20 page size
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  if (loadingLedgerGroups) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-3 fs-5">Loading Ledger Groups...</div>
      </div>
    );
  }

  return (
    <div className=" container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Ledger Group</h3>
        <div className="ledger-count">
          Total groups: <strong>{ledgerGroups.length}</strong>
        </div>
      </div>
      {(role == 'itManager' || permissions?.write) && (
        <>
          <form onSubmit={handleSubmit} className="formcontainer mb-3">
            <div className="row align-items-end">
              <div className="form-group col-md-3">
                <label htmlFor="ledgerGroupName">Ledger Group Name</label>
                <input
                  type="text"
                  id="ledgerGroupName"
                  className="form-control"
                  value={ledgerGroupName}
                  onChange={handleLedgerGroupChange}
                  placeholder="Enter Ledger Group Name"
                />
              </div>
              <div className="form-group col-md-3">
                <label htmlFor="ledgerGroupCode">Ledger Group Code</label>
                <input
                  type="text"
                  id="ledgerGroupCode"
                  className="form-control"
                  value={ledgerGroupCode}
                  onChange={handleLedgerGroupCodeChange}
                  placeholder="Enter Ledger Group Code"
                />
              </div>
              <div className="col-md-3">
                <button type="submit" className="btn btn-outline-success w-50">
                  {editIndex !== null ? 'Update Group' : 'Add Group'}
                </button>
              </div>
            </div>
          </form>
        </>
      )}
      {(role == 'itManager' || permissions?.read) && (
        <div className='formcontainer table-responsive'>
          <div className="d-flex justify-content-between gap-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search ledger groups"
              value={globalFilter || ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />


            <label className="d-inline-flex align-items-center ">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="form-select w-auto"
              >
                {[20, 30, 50, 100].map((size) => (
                  <option key={size} value={size}>
                   Show {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <table {...getTableProps()} className="table table-striped">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                      {column.render('Header')}
                      <span>
                        {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="d-flex align-items-center mb-3">
            {/* Previous – left corner */}
            <button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="btn btn-secondary me-auto"
              style={{ minWidth: 110 }}   // keeps width consistent
            >
              Previous
            </button>

            {/* Center block for page index & size selector */}
            <div className="text-center flex-grow-1 d-flex flex-column align-items-center">
              {/* Row 1: Page index and size selector in one line */}
              <div className="d-flex align-items-center gap-3">
                <div className="fw-semibold">
                  Page {pageIndex + 1} of {pageOptions.length}
                </div>

              </div>
            </div>


            {/* Next – right corner */}
            <button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="btn btn-secondary ms-auto"
              style={{ minWidth: 110 }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LedgerGroupPage;
